import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUser, FiPhone, FiMail, FiMapPin, FiUpload, FiCheck, FiX, FiLoader } = FiIcons;

const OrderForm = ({ isOpen, onClose, selectedPackage = 'ebook' }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    packageType: selectedPackage,
    address: '',
    district: '',
    province: '',
    postalCode: '',
    slip: null
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Convert file to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, slip: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const submitToGoogleSheets = async (data) => {
    // ✅ Updated Google Apps Script Web App URL - Your actual URL
    const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbwEsuX3HIGG-11cRrVUrtdiXd-wlKIjifQpXr3O7yTmRkg89brC_aOHi2xvCPShdNdrfA/exec';

    try {
      const response = await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      // Since mode is 'no-cors', we can't read the response
      // We assume success if no error is thrown
      return { success: true };
    } catch (error) {
      console.error('Error submitting to Google Sheets:', error);
      return { success: false, error: error.message };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Prepare data for Google Sheets
      const submissionData = {
        timestamp: new Date().toISOString(),
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        email: formData.email,
        packageType: formData.packageType,
        address: formData.packageType === 'hardcopy' 
          ? `${formData.address}, ${formData.district}, ${formData.province} ${formData.postalCode}` 
          : 'N/A (E-Book)',
        hasSlip: formData.slip ? 'Yes' : 'No',
        slipData: formData.slip || 'No slip uploaded', // ✅ Send actual base64 data
      };

      const result = await submitToGoogleSheets(submissionData);

      if (result.success) {
        setSubmitStatus('success');
        setTimeout(() => {
          onClose();
          setSubmitStatus(null);
          setFormData({
            firstName: '',
            lastName: '',
            phone: '',
            email: '',
            packageType: 'ebook',
            address: '',
            district: '',
            province: '',
            postalCode: '',
            slip: null
          });
        }, 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold text-[#333333]" style={{ fontFamily: 'Kanit, sans-serif' }}>
            สั่งซื้อหนังสือ
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <SafeIcon icon={FiX} className="text-2xl text-gray-500" />
          </button>
        </div>

        {/* Success/Error Messages */}
        {submitStatus === 'success' && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg mb-6 flex items-center space-x-2"
          >
            <SafeIcon icon={FiCheck} className="text-green-600" />
            <div>
              <span className="block font-semibold">ส่งคำสั่งซื้อเรียบร้อยแล้ว!</span>
              <span className="block text-sm">เราจะติดต่อกลับในไม่ช้า และสลิปการโอนจะถูกบันทึกไว้ใน Google Drive</span>
            </div>
          </motion.div>
        )}

        {submitStatus === 'error' && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6"
          >
            เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง หรือติดต่อผู้ดูแลระบบ
          </motion.div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Package Selection */}
          <div>
            <label className="block text-lg font-semibold text-[#333333] mb-3" style={{ fontFamily: 'Kanit, sans-serif' }}>
              เลือกแพ็คเกจ
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                formData.packageType === 'ebook' ? 'border-[#6082B6] bg-blue-50' : 'border-gray-300'
              }`}>
                <input
                  type="radio"
                  name="packageType"
                  value="ebook"
                  checked={formData.packageType === 'ebook'}
                  onChange={handleInputChange}
                  className="sr-only"
                />
                <div className="text-center">
                  <div className="font-semibold text-[#6082B6]">E-Book</div>
                  <div className="text-sm text-gray-600">199 บาท</div>
                </div>
              </label>

              <label className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                formData.packageType === 'hardcopy' ? 'border-[#E07A5F] bg-orange-50' : 'border-gray-300'
              }`}>
                <input
                  type="radio"
                  name="packageType"
                  value="hardcopy"
                  checked={formData.packageType === 'hardcopy'}
                  onChange={handleInputChange}
                  className="sr-only"
                />
                <div className="text-center">
                  <div className="font-semibold text-[#E07A5F]">ฉบับปกแข็ง</div>
                  <div className="text-sm text-gray-600">395 บาท (คุ้มที่สุด)</div>
                </div>
              </label>
            </div>
          </div>

          {/* Personal Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#333333] mb-2">
                <SafeIcon icon={FiUser} className="inline mr-2" />
                ชื่อ *
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6082B6] focus:border-transparent"
                placeholder="กรอกชื่อ"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#333333] mb-2">
                นามสกุล *
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6082B6] focus:border-transparent"
                placeholder="กรอกนามสกุล"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#333333] mb-2">
                <SafeIcon icon={FiPhone} className="inline mr-2" />
                เบอร์ติดต่อ *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6082B6] focus:border-transparent"
                placeholder="08X-XXX-XXXX"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#333333] mb-2">
                <SafeIcon icon={FiMail} className="inline mr-2" />
                อีเมล *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6082B6] focus:border-transparent"
                placeholder="tomorn@email.com"
              />
            </div>
          </div>

          {/* Shipping Address (only for hardcopy) */}
          {formData.packageType === 'hardcopy' && (
            <div className="space-y-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
              <h3 className="text-lg font-semibold text-[#E07A5F] flex items-center">
                <SafeIcon icon={FiMapPin} className="mr-2" />
                ที่อยู่จัดส่ง
              </h3>
              <div>
                <label className="block text-sm font-medium text-[#333333] mb-2">
                  ที่อยู่ *
                </label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  required={formData.packageType === 'hardcopy'}
                  rows="3"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E07A5F] focus:border-transparent"
                  placeholder="บ้านเลขที่ หมู่ ซอย ถนน"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#333333] mb-2">
                    อำเภอ/เขต *
                  </label>
                  <input
                    type="text"
                    name="district"
                    value={formData.district}
                    onChange={handleInputChange}
                    required={formData.packageType === 'hardcopy'}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E07A5F] focus:border-transparent"
                    placeholder="อำเภอ/เขต"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#333333] mb-2">
                    จังหวัด *
                  </label>
                  <input
                    type="text"
                    name="province"
                    value={formData.province}
                    onChange={handleInputChange}
                    required={formData.packageType === 'hardcopy'}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E07A5F] focus:border-transparent"
                    placeholder="จังหวัด"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#333333] mb-2">
                    รหัสไปรษณีย์ *
                  </label>
                  <input
                    type="text"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleInputChange}
                    required={formData.packageType === 'hardcopy'}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E07A5F] focus:border-transparent"
                    placeholder="12345"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Payment Information */}
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold text-[#333333] mb-4">ข้อมูลการชำระเงิน</h3>
            <div className="bg-white p-4 rounded-lg border border-gray-200 mb-4">
              <h4 className="font-semibold text-[#556B2F] mb-2">โอนเงินได้ที่:</h4>
              <p className="text-sm text-gray-600 mb-1">ธนาคารกสิกรไทย</p>
              <p className="text-sm text-gray-600 mb-1">เลขที่บัญชี: 123-4-56789-0</p>
              <p className="text-sm text-gray-600 mb-1">ชื่อบัญชี: บริษัท หนังสือดี จำกัด</p>
              <p className="text-sm font-semibold text-[#E07A5F]">
                ยอดที่ต้องโอน: {formData.packageType === 'ebook' ? '199' : '395'} บาท
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#333333] mb-2">
                <SafeIcon icon={FiUpload} className="inline mr-2" />
                อัปโหลดสลิปการโอนเงิน *
              </label>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                <p className="text-xs text-blue-700">
                  📁 <strong>สลิปจะถูกอัปโหลดไปยัง Google Drive อัตโนมัติ</strong><br />
                  🔗 ลิงก์ไฟล์จะถูกส่งให้ผู้ดูแลระบบทันที
                </p>
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                required
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6082B6] focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#6082B6] file:text-white hover:file:bg-[#4a6b94]"
              />
              {formData.slip && (
                <div className="mt-2 flex items-center text-green-600">
                  <SafeIcon icon={FiCheck} className="mr-2" />
                  <span className="text-sm">อัปโหลดสลิปเรียบร้อยแล้ว (พร้อมส่งไป Google Drive)</span>
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 px-6 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-[#E07A5F] hover:bg-[#d16847] text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <SafeIcon icon={FiLoader} className="animate-spin" />
                  <span>กำลังส่งและอัปโหลดสลิป...</span>
                </>
              ) : (
                <span>ยืนยันการสั่งซื้อ</span>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default OrderForm;